import { useState } from "react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { Section, useGameData } from "./GameCenter";
import "./gameplan.css";

const TIER = { starter: ["ΒΑΣ", "gp-starter"], rotation: ["ΡΟΤ", "gp-rot"] };

function PresenceStrip({ presence }) {
  return (
    <div className="gp-strip" data-testid="gp-strip">
      {presence.map((v, i) => (
        <span key={i} className={`gp-cell ${i > 0 && i % 12 === 0 ? "gp-qbreak" : ""}`}
          style={{ background: v <= 0.02 ? "#0e2742" : `rgba(79,176,255,${0.25 + v * 0.75})` }} />
      ))}
    </div>
  );
}

function PlayerRow({ p, max, metric, confirmed, onConfirm, busy }) {
  const vals = metric === "pts" ? p.pts_by_q : p.min_by_q;
  const [tag, cls] = TIER[p.tier] || ["", ""];
  const doubtful = p.espn_injury && p.espn_injury !== "Out";
  return (
    <div className="gp-player" data-testid={`gp-player-${p.player_id}`}>
      <div className="gp-player-head">
        <span className={`gp-tier ${cls}`}>{tag}</span>
        <b className="gp-name">{p.name}</b>
        {confirmed ? (
          <span role="button" tabIndex={0} className="gp-conf" data-testid={`gp-confirmed-${p.player_id}`}
            title="Επιβεβαιωμένος ότι παίζει — πάτα για αναίρεση" onClick={() => !busy && onConfirm(p.player_id, false)}>✓ Παίζει</span>
        ) : p.espn_injury ? (
          <>
            <span className={`gp-inj ${p.espn_injury === "Out" ? "out" : "dtd"}`} data-testid={`gp-inj-${p.player_id}`}>{p.espn_injury}</span>
            {doubtful && (
              <span role="button" tabIndex={0} className="gp-conf-btn" data-testid={`gp-confirm-${p.player_id}`}
                title="Επιβεβαίωσε ότι παίζει — φεύγει η αμφιβολία" onClick={() => !busy && onConfirm(p.player_id, true)}>{busy ? "…" : "Επιβεβ."}</span>
            )}
          </>
        ) : null}
        <span className="gp-tot">{p.proj_min}′ · {p.proj_pts} π</span>
      </div>
      <div className="gp-qbars">
        {vals.map((v, i) => (
          <div key={i} className="gp-qbar">
            <span className="gp-qbar-fill" style={{ height: `${max ? (v / max) * 100 : 0}%` }} />
            <small>{v}</small>
            <em>Q{i + 1}</em>
          </div>
        ))}
      </div>
      <PresenceStrip presence={p.presence} />
    </div>
  );
}

export function GamePlan({ game, isAdmin }) {
  const id = game?.id;
  const data = useGameData(`/game-plan/${id}`, isAdmin ? id : null, game?.status);
  const [metric, setMetric] = useState("pts");
  const [open, setOpen] = useState(null);
  const [ov, setOv] = useState({});
  const [busy, setBusy] = useState(null);
  if (!isAdmin) return null;
  const confirmPlay = async (teamId, pid, confirmed) => {
    setBusy(pid);
    try {
      const payload = { game_id: id, team_id: teamId, player_id: pid };
      if (confirmed) await api.post("/rotation/confirm-player", payload);
      else await api.delete("/rotation/confirm-player", { params: payload });
      setOv((o) => ({ ...o, [pid]: confirmed }));
      toast.success(confirmed ? "Επιβεβαιώθηκε ότι παίζει" : "Επαναφορά στην ένδειξη ESPN");
    } catch (e) { toast.error(errorText(e)); }
    setBusy(null);
  };
  const right = (
    <div className="gp-metric" data-testid="gp-metric">
      {[["pts", "Πόντοι"], ["min", "Λεπτά"]].map(([k, l]) => (
        <button key={k} type="button" className={metric === k ? "is-active" : ""} onClick={() => setMetric(k)} data-testid={`gp-metric-${k}`}>{l}</button>
      ))}
    </div>
  );
  return (
    <Section title="Πρόβλεψη σκοραρίσματος · ανά περίοδο" right={right} collapsible defaultOpen={false}>
      {!data ? <p className="tv-empty">Υπολογισμός πρόβλεψης…</p> : (
        <div className="gp-root" data-testid="game-plan">
          <div className="gp-teams">
            {data.teams.map((t) => {
              const mq = Math.max(...t.by_q, 1);
              return (
                <div key={t.team_id} className="gp-team" data-testid={`gp-team-${t.code}`}>
                  <div className="gp-team-top"><b>{t.code}</b><span>{t.proj_total} π</span></div>
                  <div className="gp-team-q">
                    {t.by_q.map((v, i) => (
                      <div key={i} className="gp-tq"><span className="gp-tq-fill" style={{ height: `${(v / mq) * 100}%` }} /><small>{v}</small><em>Q{i + 1}</em></div>
                    ))}
                  </div>
                  <div className="gp-halves">
                    <span data-testid={`gp-h1-${t.code}`}>Α' ημ. <b>{t.by_half[0]}</b></span>
                    <span data-testid={`gp-h2-${t.code}`}>Β' ημ. <b>{t.by_half[1]}</b></span>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="gp-legend">Η λωρίδα δείχνει πότε συνήθως είναι στο παρκέ (έντονο = συχνά) ανά λεπτό, χωρισμένη σε 4 περιόδους. Για αμφίβολους παίκτες, πάτα «Επιβεβ.» όταν ξέρεις ότι παίζει.</p>
          <div className="gp-players-wrap">
            {data.teams.map((t) => {
              const max = Math.max(...t.players.flatMap((p) => metric === "pts" ? p.pts_by_q : p.min_by_q), 1);
              const show = open === t.code ? t.players : t.players.slice(0, 5);
              return (
                <div key={t.team_id} className="gp-team-players">
                  <h4>{t.code} <span>{t.players.length} παίκτες</span></h4>
                  {show.map((p) => (
                    <PlayerRow key={p.player_id} p={p} max={max} metric={metric}
                      confirmed={ov[p.player_id] ?? p.confirmed_playing}
                      onConfirm={(pid, c) => confirmPlay(t.team_id, pid, c)} busy={busy === p.player_id} />
                  ))}
                  {t.players.length > 5 && (
                    <button type="button" className="gp-more" onClick={() => setOpen(open === t.code ? null : t.code)} data-testid={`gp-more-${t.code}`}>
                      {open === t.code ? "Λιγότερα" : `+${t.players.length - 5} ακόμη`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Section>
  );
}
